import React, { useState } from 'react';
import { supabase } from '../lib/supabase';

const OrderTrackingModal = ({ isOpen, onClose }) => {
  const [searchValue, setSearchValue] = useState('');
  const [searchType, setSearchType] = useState('phone'); // 'phone' or 'code'
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async () => {
    if (!searchValue.trim()) {
      setError('Please enter a phone number or verification code');
      return;
    }

    setLoading(true);
    setError('');
    setOrders([]);

    try {
      let query = supabase
        .from('orders')
        .select('*, customers(*), order_items(*, products(*))')
        .order('created_at', { ascending: false });

      if (searchType === 'phone') {
        // Search by phone number via customers table
        const { data: customers, error: customerError } = await supabase
          .from('customers')
          .select('id')
          .ilike('phone', `%${searchValue}%`);

        if (customerError) throw customerError;

        if (!customers || customers.length === 0) {
          setError('No orders found for this phone number');
          setLoading(false);
          return;
        }

        const customerIds = customers.map(c => c.id);
        query = query.in('customer_id', customerIds);
      } else {
        // Search by verification code
        query = query.eq('verification_code', searchValue.trim());
      }

      const { data, error: orderError } = await query;

      if (orderError) throw orderError;

      if (!data || data.length === 0) {
        setError(searchType === 'phone' 
          ? 'No orders found for this phone number'
          : 'No order found with this verification code');
      } else {
        setOrders(data);
      }
    } catch (err) {
      console.error('Error searching orders:', err);
      setError('Failed to search orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      'confirmed': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Confirmed' },
      'shipped': { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'Shipped' },
      'delivered': { bg: 'bg-green-100', text: 'text-green-800', label: 'Delivered' },
      'completed': { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Completed' },
      'cancelled': { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled' },
      'returned': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Returned' },
      'payment_failed': { bg: 'bg-red-100', text: 'text-red-800', label: 'Payment Failed' }
    };

    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      'unpaid': { bg: 'bg-red-100', text: 'text-red-800', label: 'Unpaid' },
      'paid': { bg: 'bg-green-100', text: 'text-green-800', label: 'Paid' },
      'pending': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      'refunded': { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Refunded' },
      'payment_failed': { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed' }
    };

    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-BD', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (value) => {
    if (!value) return '0.00';
    // Check if value needs division (new format * 100)
    const amount = value >= 1000 ? (value / 100) : value;
    return amount.toFixed(2);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
          style={{ boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}>
          
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-slate-700 to-slate-900 text-white p-6 rounded-t-xl flex items-center justify-between z-10">
            <div>
              <h2 className="text-2xl font-bold">Order Tracking</h2>
              <p className="text-slate-300 text-sm mt-1">Track your order status</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/10 rounded-lg"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Search Section */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search Type Toggle */}
              <div className="flex gap-2 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => {
                    setSearchType('phone');
                    setSearchValue('');
                    setOrders([]);
                    setError('');
                  }}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    searchType === 'phone'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Phone Number
                </button>
                <button
                  onClick={() => {
                    setSearchType('code');
                    setSearchValue('');
                    setOrders([]);
                    setError('');
                  }}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    searchType === 'code'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Verification Code
                </button>
              </div>

              {/* Search Input */}
              <div className="flex-1 flex gap-2">
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={searchType === 'phone' ? 'Enter phone number (e.g., 01712345678)' : 'Enter verification code (e.g., 123456)'}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-all"
                />
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className="px-6 py-3 bg-gradient-to-r from-slate-700 to-slate-900 hover:from-slate-800 hover:to-slate-900 text-white font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </div>
            </div>

            {error && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}
          </div>

          {/* Results Section */}
          <div className="p-6">
            {orders.length > 0 ? (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Found {orders.length} {orders.length === 1 ? 'order' : 'orders'}
                </h3>

                {orders.map((order) => {
                  // Calculate amounts
                  const productSubtotal = (order.order_items || []).reduce(
                    (sum, item) => sum + (item.quantity * item.unit_price), 0
                  );
                  const deliveryChargeRaw = order.delivery_charge || 0;
                  const transactionFeeRaw = order.transaction_fee || 0;
                  const needsDivisionDelivery = deliveryChargeRaw >= 1000;
                  const needsDivisionTransaction = transactionFeeRaw >= 100;
                  const deliveryCharge = needsDivisionDelivery ? (deliveryChargeRaw / 100) : deliveryChargeRaw;
                  const transactionFee = needsDivisionTransaction ? (transactionFeeRaw / 100) : transactionFeeRaw;
                  const totalAmountRaw = order.total_amount || 0;
                  const needsDivisionTotal = totalAmountRaw >= 10000;
                  const orderTotal = totalAmountRaw > 0
                    ? (needsDivisionTotal ? (totalAmountRaw / 100) : totalAmountRaw)
                    : (productSubtotal + deliveryCharge + transactionFee);

                  return (
                    <div
                      key={order.id}
                      className="bg-gradient-to-br from-white to-gray-50 rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-all"
                    >
                      {/* Order Header */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 pb-4 border-b border-gray-200">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-sm text-gray-600">Order ID:</span>
                            <span className="font-mono text-sm font-semibold text-gray-900">
                              {order.id.substring(0, 8)}...
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(order.status)}
                            {getPaymentStatusBadge(order.payment_status)}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">Order Date</div>
                          <div className="font-semibold text-gray-900">{formatDate(order.created_at)}</div>
                          {order.delivered_at && (
                            <>
                              <div className="text-xs text-gray-500 mt-1">Delivered</div>
                              <div className="text-xs text-gray-600">{formatDate(order.delivered_at)}</div>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Customer Info */}
                      {order.customers && (
                        <div className="mb-4 pb-4 border-b border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">Customer Information</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-gray-600">Name:</span>
                              <span className="ml-2 font-medium text-gray-900">{order.customers.name || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Phone:</span>
                              <span className="ml-2 font-medium text-gray-900">{order.customers.phone || 'N/A'}</span>
                            </div>
                            <div className="sm:col-span-2">
                              <span className="text-gray-600">Address:</span>
                              <span className="ml-2 text-gray-900">
                                {[order.customers.address, order.customers.upazila, order.customers.district]
                                  .filter(Boolean)
                                  .join(', ') || 'N/A'}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Order Items */}
                      <div className="mb-4 pb-4 border-b border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Order Items</h4>
                        <div className="space-y-2">
                          {(order.order_items || []).map((item, idx) => (
                            <div key={idx} className="flex justify-between items-center text-sm">
                              <span className="text-gray-900">
                                {item.quantity}× {item.products?.name_bn || 'Product'}
                              </span>
                              <span className="font-medium text-gray-900">
                                ৳{(item.quantity * item.unit_price).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Payment & Price Breakdown */}
                      <div className="mb-4 pb-4 border-b border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Price Breakdown</h4>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Product Subtotal:</span>
                            <span className="font-medium text-gray-900">৳{productSubtotal.toFixed(2)}</span>
                          </div>
                          {deliveryCharge > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Delivery Charge:</span>
                              <span className="font-medium text-gray-900">৳{deliveryCharge.toFixed(2)}</span>
                            </div>
                          )}
                          {transactionFee > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">
                                Transaction Fee ({order.payment_method === 'bkash' ? 'bKash' : order.payment_method === 'nagad' ? 'Nagad' : ''}):
                              </span>
                              <span className="font-medium text-gray-900">৳{transactionFee.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between pt-2 border-t border-gray-200 font-bold">
                            <span className="text-gray-900">Total Amount:</span>
                            <span className="text-orange-600">৳{orderTotal.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Payment Info */}
                      {order.payment_method && (
                        <div className="mb-4 pb-4 border-b border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">Payment Information</h4>
                          <div className="text-sm space-y-1">
                            <div>
                              <span className="text-gray-600">Method:</span>
                              <span className="ml-2 font-medium text-gray-900 capitalize">
                                {order.payment_method === 'bkash' ? 'bKash' : order.payment_method === 'nagad' ? 'Nagad' : order.payment_method}
                              </span>
                            </div>
                            {order.payment_transaction_id && (
                              <div>
                                <span className="text-gray-600">Transaction ID:</span>
                                <span className="ml-2 font-mono text-gray-900">{order.payment_transaction_id}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Verification Code */}
                      {order.verification_code && (
                        <div className="mb-4">
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="text-xs text-blue-700 mb-1">Verification Code</div>
                            <div className="font-mono text-lg font-bold text-blue-900">{order.verification_code}</div>
                          </div>
                        </div>
                      )}

                      {/* Notes */}
                      {order.notes && (
                        <div className="mt-4 text-sm">
                          <span className="text-gray-600 font-semibold">Notes:</span>
                          <p className="text-gray-900 mt-1">{order.notes}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : !loading && searchValue && (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-4 text-gray-600">Enter phone number or verification code to track your order</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTrackingModal;

